<template>
  <div class="mx-auto mt-10 max-w-96">
    <form class="form-wrapper" @submit.prevent="onSubmit">
      <InputWrapped
        :label="t('input.label.email')"
        :placeholder="t('input.label.email')"
        name="email"
        v-model="email"
        v-bind="emailAttrs"
        :error="errors.email"
        autocomplete="username"
        :disabled="isSendMagicLinkFetching"
      />
      <Button type="submit" class="mb-4 w-full" :disabled="isSendMagicLinkFetching">
        <Loader2 v-if="isSendMagicLinkFetching" class="mr-2 h-4 w-4 animate-spin stroke-white" />
        {{ t('common.sendMagicLink') }}
      </Button>
      <InfoBar
        v-if="sendMagicLinkError"
        variant="destructive"
        :title="t('common.error')"
        :message="sendMagicLinkError?.message"
        @close="sendMagicLinkError = null"
      />
      <InfoBar
        v-if="sendMagicLinkData"
        variant="success"
        :title="t('common.success')"
        :message="sendMagicLinkData?.message"
        @close="sendMagicLinkData = null"
      />
    </form>
  </div>
</template>

<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';

// Components
import InfoBar from '@generics/InfoBar.vue';
import InputWrapped from '@modules/InputWrapped/InputWrapped.vue';
import Button from '@ui/button/Button.vue';
import { useI18n } from 'vue-i18n';

// Icons
import { Loader2 } from 'lucide-vue-next';
import { watch } from 'vue';

const { t } = useI18n();

interface MagicLinkForm {
  email: string;
}

const accountStore = useAccountStore();
const { sendMagicLinkData, sendMagicLinkError, isSendMagicLinkFetching } =
  storeToRefs(accountStore);
const schema = toTypedSchema(
  yup.object({
    email: yup.string().required(t('errors.required')).email(t('errors.invalidEmail')),
  }),
);
const { errors, handleSubmit, defineField, values } = useForm<MagicLinkForm>({
  validationSchema: schema,
  validateOnMount: false,
  initialValues: {
    email: accountStore.guestEmail || '',
  },
});

const [email, emailAttrs] = defineField('email', {
  validateOnModelUpdate: false,
  validateOnBlur: true,
});

const onSubmit = handleSubmit((values) => {
  accountStore.sendMagicLink(values.email);
});

watch(email, (newValue) => {
  if (newValue !== accountStore.guestEmail) {
    accountStore.guestEmail = newValue;
  }
});

watch(
  values,
  () => {
    sendMagicLinkError.value = null;
    sendMagicLinkData.value = null;
  },
  { deep: true },
);
</script>
